import { useCallback, type ReactNode } from 'react';

import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import type { OfferStateCounts } from '@/modules/dashboard/services/summaries';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The tenant's OFFERS.
 *
 * One card per offer state, and the "needs attention" total — the offers a merchant still has
 * to act on (`draft` and `review`). Every number is counted from the read; none is a target, a
 * comparison against another tenant, or a guess about what the tenant "should" have.
 */
const STATE_LABEL: Record<keyof OfferStateCounts, string> = {
  draft: 'Draft',
  review: 'In review',
  published: 'Published',
  paused: 'Paused',
  archived: 'Archived',
};

const STATE_ORDER: ReadonlyArray<keyof OfferStateCounts> = [
  'published',
  'draft',
  'review',
  'paused',
  'archived',
];

export function OffersSummaryWidget({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => dashboardApi.offers(), []);

  return (
    <DashboardWidgetFrame
      title="Offers"
      load={load}
      className={className}
      isEmpty={(data) => data.total === 0}
      emptyTitle="No offers yet"
      emptyDescription="A published offer is what a customer can buy."
    >
      {(data) => (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {STATE_ORDER.map((state) => (
            <StatCard
              key={state}
              label={STATE_LABEL[state]}
              value={String(data.byState[state])}
              caption={`${data.total} offer${data.total === 1 ? '' : 's'} in total`}
            />
          ))}
          <StatCard
            label="Needs attention"
            value={String(data.needsAttention)}
            tone={data.needsAttention === 0 ? 'positive' : 'warning'}
            caption="Draft and in review"
          />
        </div>
      )}
    </DashboardWidgetFrame>
  );
}
